import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { MeResponse } from '@hospasense/contracts';
import { ApiError, apiGet } from './api.js';

interface AuthState {
  me: MeResponse | null;
  loading: boolean;
  /** True when the API explicitly said the session is absent/expired. */
  unauthenticated: boolean;
  hasPermission: (permission: string) => boolean;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * Session context. Navigation and actions render from the SAME resolved
 * permission list the API enforces — the UI never invents its own policy.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const query = useQuery<MeResponse | null>({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await apiGet<MeResponse>('/api/v1/auth/me');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
    retry: 1,
  });

  const me = query.data ?? null;
  const state: AuthState = {
    me,
    loading: query.isLoading,
    unauthenticated: !query.isLoading && me === null,
    hasPermission: (permission) => me?.permissions.includes(permission) ?? false,
    refresh: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  };
  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
